import { useParams, Navigate, Link } from "react-router-dom";
import { FileText, ArrowUpRight } from "lucide-react";
import { LEGAL_CONTENT } from "../data/legal";
import { LEGAL_PAGES } from "../data/site";
import PageHero from "../components/common/PageHero";
import Reveal from "../components/common/Reveal";
import useSeo from "../hooks/useSeo";

function LegalIndex() {
  useSeo({
    title: "Legal Centre",
    description: "The complete legal and compliance centre for Azimi & Co. Vision Recruitment — privacy, terms, policies and statements in one place.",
  });
  return (
    <>
      <PageHero
        overline="Legal"
        title="Legal Centre"
        description="Our complete set of policies, terms and statements — transparent and accessible in one place."
      />
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LEGAL_PAGES.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 70}>
                <Link
                  to={`/legal/${p.slug}`}
                  className="group flex h-full items-start justify-between gap-4 rounded-2xl border border-border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-md"
                  data-testid={`legal-index-card-${p.slug}`}
                >
                  <div className="flex items-start gap-4">
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy transition-colors group-hover:bg-gold group-hover:text-white">
                      <FileText className="h-5 w-5" />
                    </span>
                    <h3 className="mt-1 font-serif text-lg text-navy">{p.title}</h3>
                  </div>
                  <ArrowUpRight className="mt-1 h-5 w-5 text-gold transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function Legal() {
  const { slug } = useParams();
  const page = slug ? LEGAL_CONTENT[slug] : null;
  useSeo({
    title: page ? page.title : "Legal",
    description: page ? `${page.title} for Azimi & Co. Vision Recruitment.` : undefined,
  });
  if (!slug) return <LegalIndex />;
  if (!page) return <Navigate to="/legal" replace />;

  return (
    <>
      <PageHero overline="Legal" title={page.title} description={page.updated} crumb={page.title} parentCrumb={{ label: "Legal Centre", to: "/legal" }} />
      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-4 lg:px-8">
          <Reveal className="lg:col-span-3">
            <div className="rounded-2xl border border-border bg-white p-8 sm:p-10">
              <p className="rounded-lg border border-gold/20 bg-gold/5 p-4 text-xs leading-relaxed text-charcoal/70">
                Note: This policy content is provided as a professionally prepared template.
                Please review and tailor it with appropriate legal advice before publication.
              </p>
              <div className="mt-8 space-y-8">
                {page.sections.map((s) => (
                  <div key={s.heading}>
                    <h3 className="font-serif text-xl text-navy">{s.heading}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
          <aside className="lg:col-span-1">
            <div className="sticky top-28 rounded-2xl border border-border bg-background p-6">
              <h4 className="font-serif text-lg text-navy">Policies</h4>
              <ul className="mt-4 space-y-2">
                {LEGAL_PAGES.map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`/legal/${p.slug}`}
                      className={`block rounded-lg px-3 py-2 text-sm transition-colors ${
                        p.slug === slug ? "bg-navy text-white" : "text-charcoal/70 hover:bg-white hover:text-navy"
                      }`}
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
